import { FC, useCallback, useState } from 'react';
import { Loading, useDeviceDetection } from 'internalLibrary';
import { PRODUCT_NUMBER, TERM } from 'config/domain';
import { R } from 'config/app';
import { useLocation, useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import layout from '../../../../scss/layout.module.scss';
import styles from './WelcomeBack.module.scss';
import useApplicationConstraintsQuery from '../../../hooks/useApplicationConstraintsQuery';
import useIoBlackbox from 'ui/hooks/useIoBlackbox';
import useLoanApplicationProcess from 'ui/components/LoanApplicationProcessLayout/LoanApplicationProcessProvider/useLoanApplicationProcess';
import useLoanApplicationQuery from 'ui/hooks/useLoanApplicationQuery';
import usePostLoanApplicationMutation from 'ui/hooks/usePostLoanApplicationMutation';
import usePutLoanApplicationMutation from 'ui/hooks/usePutLoanApplicationMutation';
import useValidAMLExistsQuery from 'ui/hooks/useValidAMLExistsQuery';
import WelcomeBackCalculator from './WelcomeBackCalculator';
import WelcomeBackTextAndButton from './WelcomeBackTextAndButton';

const WelcomeBack: FC = () => {
  const { isMobile } = useDeviceDetection();
  const { loanAmount } = useLoanApplicationProcess();
  const ioBlackBox = useIoBlackbox();
  const { mutateAsync: postApplication, isLoading: isLoadingPostApplication } =
    usePostLoanApplicationMutation();
  const { mutateAsync: putApplication, isLoading: isLoadingPutApplication } =
    usePutLoanApplicationMutation();
  const { loanApplication, isFetching } = useLoanApplicationQuery();
  const { applicationConstraints } = useApplicationConstraintsQuery();
  const { data: validAMLData, isLoading: isLoadingValidAML } =
    useValidAMLExistsQuery(true);
  const { state: locationState } = useLocation();
  const [, setError] = useState();

  const navigate = useNavigate();

  const affiliateProvider = Cookies.get('affiliate_provider');
  const affiliateToken = Cookies.get('affiliate_token');
  const affiliatePartner = Cookies.get('affiliate_partner');

  const createAndConfirmApplication = useCallback(async () => {
    const { rejected: createRejected } = await postApplication({
      amount: loanAmount,
      term: applicationConstraints?.termInterval?.defaultValue || TERM,
      source: isMobile ? 'MOBILE' : 'DESKTOP',
      productNumber: PRODUCT_NUMBER,
      termUnit: 'DAYS',
      ioBlackBox,
      affiliateInfo:
        affiliateProvider && affiliatePartner && affiliateToken
          ? {
              provider: affiliateProvider,
              token: affiliateToken,
              partner: affiliatePartner,
            }
          : undefined,
    });

    if (createRejected) {
      navigate(R.FirstLoanRejected);
      return false;
    }

    const { rejected: confirmRejected } = await putApplication({});

    if (confirmRejected) {
      navigate(R.FirstLoanRejected);
      return false;
    }

    return true;
  }, [loanAmount]);

  const handleClick = useCallback(async () => {
    try {
      if (!validAMLData?.validAmlExists) {
        navigate(R.FirstLoanQuestionnaire);
        return;
      }

      const isNotRejected = await createAndConfirmApplication();

      if (isNotRejected) {
        navigate(R.FirstLoanIdentificationMethod);
      }
    } catch (e) {
      setError(() => {
        throw e;
      });
    }
  }, [validAMLData?.validAmlExists]);

  if (isFetching || isLoadingValidAML) {
    return (
      <div className={layout['width-lg']}>
        <div className={styles.loader}>
          <Loading />
        </div>
      </div>
    );
  }

  if (locationState === 'new' || loanApplication?.status === 'CLOSED') {
    return (
      <div className={layout['width-lg']}>
        <WelcomeBackCalculator
          buttonDisabled={
            isFetching ||
            isLoadingValidAML ||
            isLoadingPostApplication ||
            isLoadingPutApplication
          }
          onButtonClick={handleClick}
        />
      </div>
    );
  }

  return (
    <div className={layout['width-lg']}>
      <WelcomeBackTextAndButton
        loanAmount={loanAmount}
        onClick={handleClick}
        buttonDisabled={
          isLoadingPostApplication ||
          isLoadingPutApplication ||
          isFetching ||
          isLoadingValidAML
        }
      />
    </div>
  );
};

export default WelcomeBack;
